import {
  shouldAutoCheckCommercialBacResult,
  shouldAutoCheckIntentBacResult,
} from "./intentToSuspendData";

const OBSERVED_ATTRIBUTES = ["form-data"];

class IntentToSuspend extends HTMLElement {
  static get observedAttributes() {
    return OBSERVED_ATTRIBUTES;
  }

  constructor() {
    super();
    this.attachShadow({ mode: "open" });
  }

  connectedCallback() {
    this.render();
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (OBSERVED_ATTRIBUTES.includes(name) && oldValue !== newValue) {
      this.render();
    }
  }

  parseJsonAttribute(name, fallback = {}) {
    const value = this.getAttribute(name);
    if (!value) return fallback;
    try {
      return JSON.parse(value);
    } catch (e) {
      console.error(`Invalid JSON in attribute ${name}`, e);
      return fallback;
    }
  }

  formatDate(value) {
    if (!value) return "";

    if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
      const [year, month, day] = value.split("-");
      return `${month}/${day}/${year}`;
    }

    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    const month = `${date.getMonth() + 1}`.padStart(2, "0");
    const day = `${date.getDate()}`.padStart(2, "0");
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  }

  checkedBox(checked, className = "") {
    return `<span class="box ${className}">${checked ? "X" : ""}</span>`;
  }

  escapeHtml(value) {
    if (value === null || value === undefined) return "";

    return String(value)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");
  }

  gridCell(label, value, colStart, colEnd, rowStart, rowEnd, extra = "") {
    return `
      <div class="cell ${extra}" style="grid-column:${colStart}/${colEnd};grid-row:${rowStart}/${rowEnd};">
        <div class="label static-text">${label}</div>
        <div class="fill-text">${this.escapeHtml(value)}</div>
      </div>
    `;
  }

  inlineGridCell(label, value, colStart, colEnd, rowStart, rowEnd, extra = "") {
    return this.gridCell(
      label,
      value,
      colStart,
      colEnd,
      rowStart,
      rowEnd,
      `inline-data-cell ${extra}`.trim(),
    );
  }

  inlineFill(value, className = "") {
    return `<span class="inline-fill fill-text ${className}">${this.escapeHtml(value)}</span>`;
  }

  dateFill(value, className = "") {
    return this.inlineFill(this.formatDate(value), className);
  }

  shouldAutoCheckIntentBacResult(data) {
    return shouldAutoCheckIntentBacResult(data);
  }

  shouldAutoCheckCommercialBacResult(data) {
    return shouldAutoCheckCommercialBacResult(data);
  }

  resolveFormData(data) {
    return {
      ...data,
      commercialAlcoholConcentration:
        data?.commercialAlcoholConcentration || data?.alcoholConcentration || "",
      testResultAtOrAboveLimit:
        !!data?.testResultAtOrAboveLimit ||
        this.shouldAutoCheckIntentBacResult(data),
      commercialTestAtOrAboveLimit:
        !!data?.commercialTestAtOrAboveLimit ||
        this.shouldAutoCheckCommercialBacResult(data),
    };
  }

  signatureLine(label, value, className = "") {
    return `
      <div class="signature ${className}">
        <span class="signature-line fill-text">${this.escapeHtml(value)}</span>
        <span class="signature-label static-text">${label}</span>
      </div>
    `;
  }

  renderPageOne(data) {
    return `
      <article class="page">
        <section class="form-grid page-one-grid">
        <div class="title-block centered" style="grid-column:1/72;grid-row:1/2;">
          <div class="serif-text">STATE OF VERMONT</div>
        </div>
          <div class="title-block left" style="grid-column:3/30;grid-row:2/4;">
            <div class="serif-text">SUPERIOR COURT</div>
          </div>
          <div class="title-block left" style="grid-column:19/30;grid-row:4/6;">
            <div class="serif-text">Unit</div>
          </div>
          <div class="title-block left" style="grid-column:48/72;grid-row:2/4;">
            <div class="serif-text">CRIMINAL DIVISION</div>
          </div>
          <div class="title-block left" style="grid-column:48/72;grid-row:4/6;">
            <div class="serif-text">Incident No.</div>
          </div>
          <div class="font-large left" style="grid-column:48/72;grid-row:6/8;">
            <div class="serif-text">Department</div>
          </div>
          <div class="font-large left" style="grid-column:48/72;grid-row:8/10;">
            <div class="serif-text">Date of Incident</div>
          </div>
          <div class="font-large left" style="grid-column:48/72;grid-row:10/12;">
            <div class="serif-text">Time</div>
          </div>

          ${this.gridCell("", data.unit, 3, 19, 4, 6, "no-border")}
          ${this.gridCell("", data.incidentNumber, 58, 72, 4, 6, "underline-cell")}
          ${this.gridCell("", data.department, 57, 72, 6, 8, "underline-cell")}
          ${this.gridCell("", this.formatDate(data.dateOfIncident), 60, 72, 8, 10, "underline-cell")}
          ${this.gridCell("", data.timeOfIncident, 52, 58, 10, 12, "underline-cell")}
          
          <div class="cell check-cell no-border" style="grid-column:58/65;grid-row:10/12;">
            <div class="vert-box serif-text">${this.checkedBox(data.timeOfIncidentMeridiem === "AM")} AM</div>
          </div>
          <div class="cell check-cell no-border" style="grid-column:65/72;grid-row:10/12;">
            <div class="vert-box serif-text">${this.checkedBox(data.timeOfIncidentMeridiem === "PM")} PM</div>
          </div>

          <div class="title-block static-text centered" style="grid-column:8/66;grid-row:14/22;">
            <div class="serif-text">NOTICE OF INTENTION TO SUSPEND</div>
            <div class="serif-text">AND / OR</div>
            <div class="serif-text">DISQUALIFY DRIVER'S LICENSE OR PRIVILEGE TO OPERATE</div>
            <div class="subtitle serif-text">(Pursuant to Sections 1205, 1218 and/or 4116 of Title 23 of the Vermont Statutes)</div>
          </div>

          ${this.gridCell("Name", data.name, 2, 31, 27, 31)}
          ${this.gridCell("Driver's License #", data.driversLicenseNo, 31, 47, 27, 31, "")}
          ${this.gridCell("State", data.driversLicenseState, 47, 60, 27, 31, "")}
          <div class="cell check-cell end-cell" style="grid-column:60/72;grid-row:27/31;">
            <span class="label static-text">CDL</span>
            <span class="instructions">${this.checkedBox(data.isCdl, "small-box")} Yes</span>
            <span class="instructions">${this.checkedBox(!data.isCdl, "small-box")} No</span>
          </div>

          ${this.gridCell("Mailing Address", data.mailingStreet, 2, 47, 31, 35)}
          
          <div class="cell check-cell end-cell" style="grid-column:47/72;grid-row:31/33;">
            <span class="label static-text" style="margin-right: 8pt;">Driving Commercial Vehicle?</span>
            <span class="instructions">${this.checkedBox(data.isCommercialVehicle, "small-box")} Yes</span>
            <span class="instructions">${this.checkedBox(!data.isCommercialVehicle, "small-box")} No</span>
          </div>
  

          <div class="cell check-cell topless end-cell" style="grid-column:47/72;grid-row:33/35;">
            <span class="label static-text" style="margin-right: 40pt;">Hazardous Material?</span>
            <span class="instructions">${this.checkedBox(data.isHazmat, "small-box")} Yes</span>
            <span class="instructions">${this.checkedBox(!data.isHazmat, "small-box")} No</span>
          </div>
          
          ${this.gridCell("Town/City", data.townCity, 2, 31, 35, 39)}
          ${this.gridCell("State + Zip Code", [data.mailingState, data.zipCode].filter(Boolean).join(" "), 31, 47, 35, 39)}
          ${this.gridCell("Social Security #", data.socialSecurityNumber, 47, 60, 35, 39)}
          ${this.gridCell("US DOT#", data.usDotNumber, 60, 72, 35, 39, "end-cell")}

          ${this.inlineGridCell("Date of Birth", this.formatDate(data.dateOfBirth), 2, 31, 39, 41)}
          ${this.inlineGridCell("Place of Birth", data.placeOfBirth, 2, 31, 41, 43, "topless bottom-cell")}
          ${this.gridCell("Age", data.age, 31, 47, 39, 43, "bottom-cell")}
          ${this.gridCell("Home Phone", data.phoneHome, 47, 60, 39, 43, "bottom-cell")}
          ${this.gridCell("Cell Phone", data.phoneCell || data.cellPhone || data.phoneWork, 60, 72, 39, 43, "end-cell bottom-cell")}

          <span style="grid-column:3/5;grid-row:45/49;">${this.checkedBox(data.isFirstViolation)}</span>
          <p class="instructions" style="grid-column:5/71;grid-row:45/49;"> This is your first violation of 23 V.S.A. section 1201: Effective date of suspension is set forth below unless a hearing is requested.</p>

          <span style="grid-column:3/5;grid-row:50/54;">${this.checkedBox(data.isSecondOrSubsequentViolation)}</span>
          <p class="instructions" style="grid-column:5/72;grid-row:50/54;"> This is your 2nd or subsequent violation of 23 V.S.A. section 1201 or 2nd subsequent suspension under 23 V.S.A. section 1205 after July 1, 1991: Effective date of suspension is set forth below, even if you request a hearing.</p>
   
          <span style="grid-column:3/5;grid-row:55/61;">${this.checkedBox(data.bacWithPreviousConviction)}</span>
          <p class="instructions" style="grid-column:5/71;grid-row:55/61;"> You operated a motor vehicle with a blood alcohol concentration (BAC) of 0.02% or more after previous conviction within the preceding three years with a BAC for the second or subsequent was proven to be 0.16% or greater pursuant to 23 V.S.A 1201(d)(2).</p>

          <span style="grid-column:3/5;grid-row:62/64;">${this.checkedBox(data.refusedBreathOrBloodTest)}</span>
          <p class="instructions" style="grid-column:5/73;grid-row:62/64;"> You refused to submit to the breath or blood test (license suspension of at least six months).</p>
          
          <span style="grid-column:3/5;grid-row:65/70;">${this.checkedBox(data.testResultAtOrAboveLimit)}</span>
          <p class="instructions" style="grid-column:5/73;grid-row:65/70;"> The results of the breath or blood test show that your alcohol concentration was ${this.inlineFill(data.alcoholConcentration, "narrow")} or more, equal to or more than 0.08%, or 0.02% if operating a school bus or after previous conviction of 0.16% or greater (license suspension of at least 90 day).</p>

          <span style="grid-column:3/5;grid-row:71/73;">${this.checkedBox(data.commercialRefusal)}</span>
          <p class="instructions" style="grid-column:5/73;grid-row:71/73;"> You operated a commercial vehicle and refused to submit to the breath or blood test.</p>

          <span style="grid-column:3/5;grid-row:74/78;">${this.checkedBox(data.commercialTestAtOrAboveLimit)}</span>
          <p class="instructions" style="grid-column:5/71;grid-row:74/78;"> You operated a commercial vehicle and submitted to a breath or blood test which shows your alcohol concentration was ${this.inlineFill(data.commercialAlcoholConcentration, "narrow")} or more while operating a commercial vehicle, equal to more than 0.04%.</p>

          <p class="instructions" style="grid-column:5/71;grid-row:78/80;">THEREFORE, the Commissioner of Motor Vehicles will be notified to</p>
          
          <span style="grid-column:5/7;grid-row:80/82;">${this.checkedBox(data.suspendLicense)} </span>
          <p class="instructions" style="grid-column:7/71;grid-row:80/82;">SUSPEND your driver's license or privilege to operate a motor vehicle.</p>
          
          <span style="grid-column:5/7;grid-row:82/86;">${this.checkedBox(data.disqualifyCommercialDriver)} </span>
          <p class="instructions" style="grid-column:7/71;grid-row:82/86;">DISQUALIFY you as a commercial driver for a period of at least one year (if first offense) or for your lifetime (if this is your second or subsequent offense) and until all requirements are met for license reinstatement.</p>

          <p class="instructions" style="grid-column:2/72;grid-row:88/93;">Unless this is the first offense and you request a hearing to argue that your license should not be suspended or disqualified, your driver's license, commercial driver's license and/or your privilege to operate a motor vehicle is suspended and/or disqualified beginning ${this.dateFill(data.suspensionBeginsOn, "wide")}.</p>
          <p class="instructions italic" style="grid-column:26/73;grid-row:93/94;">(date suspension and/or disqualification begins)</p>
        
          <div class="revision form-footer static-text" style="grid-column:1/60;grid-row:103/105;">
            200-00240 - Notice of Intention to Suspend (10/2024)
          </div>
          <div class="revision form-footer static-text" style="grid-column:66/73;grid-row:103/105;">
            Page 1 of 3
          </div>
        </section>
      </article>
    `;
  }

  renderPageTwo(data) {
    return `
      <article class="page">
        <section class="form-grid page-two-grid">
          <div style="grid-column:2/72;grid-row:3/12;">
            <p class="instructions"><strong>If this is your first violation of 23 V.S.A. Section 1201, and you do not request a hearing, your license will be suspended as provided in this notice. If after July 1, 1991, you were convicted of a violation of 23 V.S.A. Section 1201 or had your license suspended pursuant to 23 V.S.A. Section 1205, your license will be suspended on the 11th day after you receive this notice, whether or not you request a hearing. It is a crime to operate, attempt to operate or be in actual physical control of a vehicle while your privilege to operate is suspended.</strong></p>
          </div>

            <p class="instructions" style="grid-column:2/3;grid-row:14/20;">1.)</p>
            <p class="instructions" style="grid-column:4/72;grid-row:14/20;">YOU HAVE THE RIGHT TO ASK FOR A HEARING BEFORE THE COURT TO CONTEST THE SUSPENSION AND/OR DISQUALIFICATION OF YOUR OPERATOR'S LICENSE. IF JUDGMENT IS ENTERED AGAINST YOU IN THIS PROCEEDING, YOU WILL BE ASSESSED COURT COSTS OF $90.00.</p>
            
            <p class="instructions" style="grid-column:2/3;grid-row:20/22;">2.)</p>
            <p class="instructions" style="grid-column:4/72;grid-row:20/22;">
            If you do not request a hearing, your license will be suspended and/or disqualified as provided in this notice.
            </p>
      
            <p class="instructions" style="grid-column:2/3;grid-row:23/27;">3.)</p>
            <p class="instructions" style="grid-column:4/72;grid-row:23/27;">
            If you wish to request a hearing before the Court, you must mail or deliver your request for a hearing to the Commissioner of Motor Vehicles by: ${this.dateFill(data.dateRequestDue, "medium")} <span class="line-caption inline-caption">(Date Request Due)</span>
            </p>
            
            <p class="instructions" style="grid-column:2/3;grid-row:28/34;">4.)</p>
            <p class="instructions" style="grid-column:4/72;grid-row:28/34;">
              If your request for a hearing is not mailed or delivered to the Commissioner of Motor Vehicles by ${this.dateFill(data.dateRequestDue, "medium")} <span class="line-caption inline-caption">(Date Request Due)</span>, you waive your right to a hearing and your license will be suspended and/or disqualified as provided in this notice.
            </p>
    
            <p class="instructions" style="grid-column:2/3;grid-row:35/39;">5.)</p>
            <p class="instructions" style="grid-column:4/72;grid-row:35/39;">
              In order to request a hearing, sign the section of this form and mail or deliver the form to the Commissioner of Motor Vehicles at 120 State Street, Montpelier, VT 05603.
            </p>
         
            <p class="instructions" style="grid-column:2/3;grid-row:40/56;">6.)</p>
            <p class="instructions" style="grid-column:4/72;grid-row:40/56;">
              If you request a hearing, you or your attorney must appear at a preliminary hearing at the Court in: ${this.inlineFill(data.circuitCounty, "hearing-fill")} 
              <span class="instructions italic">(county)</span>
              ${this.inlineFill(data.courtAddress, "hearing-fill address")} 
              <span class="instructions italic">(court address)</span>, on
              ${this.dateFill(data.hearingDate, "hearing-fill short")} 
              <span class="instructions italic">(date)</span>
              at ${this.inlineFill(data.hearingTime, "hearing-fill short")} 
              <span class="instructions italic">(time)</span>
              ${this.checkedBox(data.hearingMeridiem === "AM")} AM 
              ${this.checkedBox(data.hearingMeridiem === "PM")} PM.
              <br/>
              This hearing will be scheduled to take place within 21 days from the date of the offense.
            </p>
          </div>

          <div class="service-copy" style="grid-column:4/72;grid-row:49/58;">
            <p class="instructions">This notice was ${this.checkedBox(data.noticeDeliveryMethod === "hand")} hand delivered ${this.checkedBox(data.noticeDeliveryMethod === "mail")} mailed to the operator on ${this.dateFill(data.noticeIssuedDate, "short")} <span class="line-caption">(date issued)</span>.</p>
            <p class="instructions">An affidavit describing the reasons the breath test was requested by the officer:</p>
            <p class="indent instructions">${this.checkedBox(data.affidavitDeliveryMethod === "included")} was given or mailed to the defendant with this notice.</p>
            <p class="indent instructions">${this.checkedBox(data.affidavitDeliveryMethod === "later")} will be mailed at a later date.</p>
          </div>

          <div class="officer-lines" style="grid-column:3/71;grid-row:60/101;">
            <div class="officer-row static-text">
              <span>Officer's Signature:</span>
              <span class="signature-line fill-text">${this.escapeHtml(data.officerSignature || "")}</span>
              <span>/ Officer's Number:</span>
              <span class="signature-line officer-number fill-text">${this.escapeHtml(data.officerNumber)}</span>
            </div>
            <br/><br/>
            <div class="officer-row static-text">
              <span>Officer's Printed Name:</span>
              <span class="signature-line fill-text" style="margin-right: 185pt;">${this.escapeHtml(data.officerName)}</span>
            </div>
          </div>
          <div class="revision form-footer static-text" style="grid-column:1/60;grid-row:103/105;">
            200-00240 - Notice of Intention to Suspend (10/2024)
          </div>
          <div class="revision form-footer static-text" style="grid-column:66/73;grid-row:103/105;">
            Page 2 of 3
          </div>
        </section>
      </article>
    `;
  }

  renderPageThree(data) {
    return `
      <article class="page">
        <section class="form-grid page-three-grid">
          <div style="grid-column:2/72;grid-row:5/13;">
            <p class="instructions">If you are detained, arrested, lodged or released upon citation for a second or subsequent violation of 23 VSA 1201, no person shall sell, transfer, or encumber the title to a vehicle that may be subject to immobilization or forfeiture unless approved by the court in which the charge is filed for good cause shown.</p>
          </div>

          <div class="centered" style="grid-column:2/72;grid-row:12/14;">
            <p class="instructions"><strong>REQUEST FOR HEARING REGARDING INTENTION TO SUSPEND</strong></p>
          </div>
          <div class="instructions centered" style="grid-column:2/72;grid-row:14/18;">
            <p class="instructions"><strong>To Request a Hearing, sign the bottom of this form and mail it in the envelope provided or deliver it within seven days to:</strong></p>
          </div>
          <div class="instructions centered" style="grid-column:2/72;grid-row:18/20;">
            <p class="instructions"><strong>Commissioner of Motor Vehicles, Attention: Driver Improvement</strong></p>
          </div>
          <div class="instructions centered" style="grid-column:2/72;grid-row:20/22;">
            <p class="instructions"><strong>120 State St., Montpelier, VT 05603.</strong></p>
          </div>
          <div class="instructions centered" style="grid-column:2/72;grid-row:22/24;">
            <p class="instructions"><strong>Keep a copy for your records.</strong></p>
          </div>
          <p class="instructions" style="grid-column:2/72;grid-row:26/28;">IF YOU REQUEST A HEARING by the date indicated on the Notice of Intention to Suspend:</p>

          <p class="instructions" style="grid-column:3/4;grid-row:29/38;">1.</p>
          <p class="instructions" style="grid-column:5/72;grid-row:29/38;">If after July 1, 1991, you have not been convicted of a violation of Operating a Vehicle while under the Influence (23 V.S.A. section 1201) or had your operator's license suspended pursuant to 23 V.S.A. section 1205, or if you are facing a violation of Operating a Commercial Vehicle while under the Influence (23 V.S.A. section 1218 and/or 4116), your license will not be suspended or your privilege to operate a motor vehicle will not be taken away until after the final hearing on the suspension, and then only if the judge decides against you.</p>
          
          <p class="instructions" style="grid-column:3/4;grid-row:39/55;">2.</p>
          <p class="instructions" style="grid-column:5/72;grid-row:39/55;">
          A preliminary hearing will be held at the time and place given on the Notice on the front of this Request. <strong>THIS IS THE ONLY NOTICE YOU WILL RECEIVE FOR THIS HEARING.</strong> At the preliminary hearing, the judge will tell you about your rights. You will also have a chance to see all affidavits, reports, breath or blood test results, and any other written testimony or evidence that the State plans to use at the final hearing, and to learn the names and addresses of any of the State's witnesses. Likewise, you must bring with you to the preliminary hearing any reports, breath or blood test results, and any other written testimony or evidence that you plan to use at the final hearing for the State to look at. You must also bring the names and addresses of any witnesses you plan to use. <strong>ANY EVIDENCE OR WITNESSES' NAMES NOT BROUGHT TO THE PRELIMINARY HEARING FOR INSPECTION BY THE STATE MAY NOT BE ALLOWED TO BE USED IN THE FINAL HEARING.</strong>
          </p>
         
          <p class="instructions" style="grid-column:3/4;grid-row:56/70;">3.</p>
          <p class="instructions" style="grid-column:5/72;grid-row:56/70;"> Either you or your attorney must appear at this preliminary hearing. If you do not have an attorney who can appear for you, and it would create a major hardship for you to appear, you must write to the court telling them why it would create a hardship and ask to be excused. <strong>IF YOU DO NOT ATTEND AND HAVE NOT BEEN GRANTED AN EXCUSE BY THE COURT, THE COURT MAY ENTER A JUDGMENT AGAINST YOU AND NOTIFY THE COMMISSIONER OF MOTOR VEHICLES TO SUSPEND YOUR LICENSE, IF YOUR LICENSE HAS NOT ALREADY BEEN SUSPENDED BY OPERATION OF LAW.</strong> At the end of the preliminary hearing, the court will give you a time and date for the final hearing on suspension, which will be held within 21 days after the preliminary hearing.
          </p>
         
          <p class="instructions" style="grid-column:3/4;grid-row:71/81;">4.</p>
          <p class="instructions" style="grid-column:5/72;grid-row:71/81;">At the final hearing on the suspension, you and the State will present your evidence and arguments to the judge. For information regarding the issues to be determined at the final hearing, see Title 23, Sections 1202 and 1205 (of the Vermont Statutes Annotated). If the Court rules against you at this hearing, the court will notify the Commissioner of Motor Vehicles, who will then suspend your license or privilege to operate a motor vehicle. You will be assessed court costs of $90.00. If the court rules in your favor at this hearing, the court will notify the Department of Motor Vehicles, you will be reinstated if you meet all the reinstatement requirements.
          </p>
 
          <div class="centered" style="grid-column:1/73;grid-row:82/86;">
           <p class="instructions"> <strong>I REQUEST A HEARING IN THE COURT TO CONTEST THE SUSPENSION OF MY DRIVER'S LICENSE OR PRIVILEGE TO OPERATE A MOTOR VEHICLE.</strong></p>
          </div>


          ${this.gridCell("Date", this.formatDate(data.requestDate), 2, 28, 86, 90)}
          ${this.gridCell("Signature", data.requestSignature, 28, 72, 86, 90, "end-cell")}
          ${this.gridCell("Home Address", data.requestHomeAddress || data.mailingStreet, 2, 72, 90, 94, "end-cell")}
          
          ${this.gridCell("Town/City", data.requestTownCity || data.townCity, 2, 28, 94, 98, "bottom-cell")}
          ${this.gridCell("State + Zip Code", [data.requestState || data.mailingState, data.requestZipCode || data.zipCode].filter(Boolean).join(" "), 28, 40, 94, 98, "bottom-cell")}
          ${this.gridCell("Home Phone", data.requestPhoneHome || data.phoneHome, 40, 60, 94, 98, "bottom-cell")}
          ${this.gridCell("Cell Phone", data.requestPhoneCell || data.phoneCell || data.cellPhone || data.phoneWork, 60, 72, 94, 98, "bottom-cell end-cell")}

    

          <p class="instructions centered" style="grid-column:1/73;grid-row:100/103;"><strong>PLEASE NOTE: THIS IS A CIVIL HEARING. CRIMINAL CHARGES MAY ALSO BE BROUGHT AGAINST YOU.</strong></p>
          
          <div class="revision form-footer static-text" style="grid-column:1/60;grid-row:103/105;">
            200-00240 - Notice of Intention to Suspend (10/2024)
          </div>
          <div class="revision form-footer static-text" style="grid-column:66/73;grid-row:103/105;">
            Page 3 of 3
          </div>
        </section>
      </article>
    `;
  }

  render() {
    const data = this.resolveFormData(this.parseJsonAttribute("form-data", {}));

    this.shadowRoot.innerHTML = `
      <style>
        @page {
          size: Letter portrait;
          margin: 0.25in;
        }

        * {
          box-sizing: border-box;
          color: #000;
          font-family: Arial, Helvetica, sans-serif;
          margin: 0;
        }

        :host {
          --border: 1pt solid #000;
          --font-title: 14pt;
          --font-large: 12pt;
          --font-body: 10.5pt;
          --font-small: 7pt;
          --font-label: 9pt;
          --font-fill: 12pt;
          --font-revision: 8pt;
          --line-tight: 1.1;
          --line-body: 1.18;
          background: transparent;
          display: block;
        }

        .static-text {
          font-size: var(--font-body);
          line-height: var(--line-body);
        }

        .font-large {
          font-size: var(--font-large);
          font-weight: 700;
        }

        .page {
          background: #fff;
          height: 11in;
          overflow: hidden;
          page-break-after: always;
          padding: 0.25in;
          width: 8.5in;
        }

        .page:last-child {
          page-break-after: auto;
        }

        .form-grid {
          align-content: stretch;
          display: grid;
          grid-template-columns: repeat(72, 1fr);
          grid-template-rows: repeat(104, 1fr);
          height: 100%;
          width: 100%;
        }

        .revision {
          align-self: start;
          font-size: var(--font-revision);
          line-height: 1;
        }

        .form-footer {
          align-self: end;
          padding-bottom: 1pt;
        }

        .title-block {
          font-size: var(--font-title);
          font-weight: 700;
          line-height: var(--line-tight);
        }

        .serif-text {  
          font-family: Cambria, "Times New Roman", Times, serif;
        }

        .centered {
          text-align: center;
        }
        
        .left {
          text-align: left;
        }

        .title-block .subtitle {
          font-size: var(--font-fill);
          margin-top: 2pt;
          font-weight: 700;
        }

        .cell {
          border-left: var(--border);
          border-top: var(--border);
          display: flex;
          flex-direction: column;
          min-width: 0;
          overflow: hidden;
          padding: 1pt 2pt;
        }

        .underline-cell {
          border-bottom: var(--border);
          border-left: 0;
          border-right: 0;
          border-top: 0;
        }

        .end-cell {
          border-right: var(--border);
        }
        
        .bottom-cell {
          border-bottom: var(--border);
        }
        
        .topless {
          border-top: 0;
        }

        .no-border {
          border: 0;
        }

        .label {
          font-size: var(--font-label);
          line-height: 1;
          white-space: nowrap;
        }

        .inline-data-cell {
          align-items: center;
          flex-direction: row;
          gap: 4pt;
        }

        .inline-data-cell .label {
          flex: 0 0 auto;
        }

        .fill-text {
          align-items: center;
          display: flex;
          flex: 1;
          font-size: var(--font-fill);
          line-height: 1.05;
          min-height: 0;
          min-width: 0;
          overflow: hidden;
          padding-top: 1pt;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .inline-data-cell .fill-text {
          font-size: 9.5pt;
          justify-content: flex-start;
          padding-top: 0;
        }

        .check-cell {
          align-items: center;
          flex-direction: row;
          gap: 5pt;
          white-space: nowrap;
        }

        .box {
          align-items: center;
          border: var(--border);
          display: inline-flex;
          flex: 0 0 auto;
          font-size: var(--font-small);
          font-weight: 700;
          height: 12pt;
          justify-content: center;
          line-height: 1;
          margin: 0 2pt 0 0;
          vertical-align: text-top;
          width: 12pt;
        }

        .small-box {
          height: 10pt;
          width: 10pt;
        }
        
        .vert-box {
          display: inline-flex;
          align-items: center;
        }

        .instructions {
          font-size: var(--font-body);
          line-height: var(--line-body);
        }

        .italic {
          font-style: italic;
        }

        .inline-fill {
          border-bottom: var(--border);
          display: inline-flex;
          justify-content: center;
          min-height: 10pt;
          min-width: 14ch;
          padding: 0 2pt;
          vertical-align: baseline;
        }

        .inline-fill.narrow {
          min-width: 8ch;
        }

        .inline-fill.short {
          min-width: 13ch;
        }

        .inline-fill.medium {
          min-width: 20ch;
        }

        .inline-fill.wide {
          min-width: 34ch;
        }

        .line-caption {
          font-size: var(--font-small);
          line-height: 1;
        }

        .inline-caption {
          white-space: nowrap;
        }

        .service-copy {
          font-size: 8.5pt;
          line-height: 1.3;
        }

        .service-copy p {
          margin-bottom: 3pt;
        }

        .hearing-fill {
          min-width: 24ch;
        }

        .hearing-fill.address {
          min-width: 30ch;
        }

        .hearing-fill.short {
          min-width: 14ch;
        }

        .indent {
          padding-left: 16pt;
        }

        .officer-lines {
          display: flex;
          flex-direction: column;
        }

        .officer-row {
          align-items: baseline;
          display: grid;
          gap: 5pt;
          grid-template-columns: auto 1fr auto 0.35fr;
        }

        .officer-row:last-child {
          grid-template-columns: auto 1fr;
        }

        .signature-line {
          border-bottom: var(--border);
          display: inline-flex;
          min-height: 13pt;
          min-width: 0;
        }

        .officer-number {
          min-width: 8ch;
        }

        @media print {
          .page {
            border: 0;
            margin: 0;
            padding: 0;
          }

          .form-grid {
            height: 10.5in;
            margin: 0.25in;
            width: 8in;
          }
        }

        @media screen {
          .page {
            border: 1px solid #666;
            box-shadow: 0 1px 4px rgba(0, 0, 0, 0.18);
            margin: 0 auto 40px;
            position: relative;
          }

          .page:not(:last-child)::after {
            background: #d8d8d8;
            border-bottom: 1px dashed #555;
            border-top: 1px dashed #555;
            bottom: -25px;
            color: #333;
            content: "page break";
            font: 11px/18px Arial, Helvetica, sans-serif;
            height: 18px;
            left: 0;
            letter-spacing: 0;
            position: absolute;
            right: 0;
            text-align: center;
            text-transform: uppercase;
          }
        }
      </style>

      ${this.renderPageOne(data)}
      ${this.renderPageTwo(data)}
      ${this.renderPageThree(data)}
    `;
  }
}

if (!customElements.get("intent-to-suspend")) {
  customElements.define("intent-to-suspend", IntentToSuspend);
}

if (import.meta.env.DEV && !import.meta.env.VITEST && import.meta.hot) {
  import.meta.hot.accept(() => {
    window.location.reload();
  });
}
